import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ARTIFACT_HATCH_CSS, ARTIFACT_LABELS, ARTIFACT_LAYER, ARTIFACT_SPECIMEN, ARTIFACT_STRIPE, FERTILE_TYPES, LAYERS,
  LAYER_COLOURS, addShape, boundingBox, clamp01, dedupe, distance, removeLayer, simplifyPath, specimenIndexes,
} from '../../utils/annotationGeometry';
import {
  analyseImage, analysisSize, captureLoop, cleanMask, countMask, fetchPixelImage, magicWand, maskToShapes,
  rasterizeShapes,
} from '../../utils/annotation/raster';
import { costFromGradient, liveWire, snapPoint } from '../../utils/annotation/livewire';
import {
  beginSegment, emptyTrace, endSegment, extendTrace, findClosure, placeCursor, tracePoints, undoSegment,
} from '../../utils/annotation/trace';
import VirtualJoystick from './VirtualJoystick';
import Loupe from './Loupe';
import { wheelFactor } from '../../utils/panZoom';
import { ToolButton, ToolOptions, WorkbenchSlot } from '../workbench/WorkbenchContext';
import { FloatCheck, FloatRange, ReachSlider } from '../workbench/FloatControls';
import WorkbenchDrawer from '../workbench/WorkbenchDrawer';

/*
 * Paint, outline, trace or magic-fill the parts of each mushroom in a photograph.
 *
 * Controlled: `value` is the list of entries ({specimen_index, layer,
 * fertile_type, geometry: {shapes}}) and every edit calls onChange with a new
 * list. Undo and redo keep previous lists.
 *
 * Tools
 *   brush    drag to paint (touch, pen or mouse -- pointer events)
 *   outline  click around the part; click the first point, double-click or
 *            press Enter to close; Esc cancels; Backspace removes a point
 *   eraser   drag to cut paint away from the current specimen's current part
 *   trace    steer a cursor around the part -- arrow keys / WASD, a mouse drag,
 *            a finger (the cursor sits above it, with a magnifier) or the
 *            joystick. Closing the loop, or Space, fills what it encloses
 *            (utils/annotation/raster.js captureLoop); with "claim enclosed
 *            areas" on, leaving this part's paint and coming back fills the gap.
 *            Backspace takes back the last movement only.
 *   magic    click a part to fill everything that looks like it (colour, edges,
 *            texture); Shift+click adds, Alt+click cuts away; [ and ] change the
 *            tolerance and redo the last click.
 * The magnet snaps tracing to edges: a live wire for mouse and finger, local
 * snapping for keys and joystick. Hold Alt to ignore it. Magic fill and the
 * magnet read the photograph's pixels; where the browser does not allow that
 * (a cross-origin photo without CORS) they switch off and say so.
 *
 * The canvas is drawn at the photograph's own proportions (at most 1600 px on
 * the long side) and stretched to the displayed image. Tracing and filling work
 * in "analysis" pixels (at most 1024 on the long side); everything is stored
 * normalised to 0..1. Hotkeys use physical key positions, so they work on a
 * Hebrew keyboard layout too, and only while the canvas has focus.
 *
 * Magnifying (2026-10-15): the zoom is the layout's own (the picture is laid
 * out wider inside a scrolling frame, so every tap still maps exactly, Loupe
 * included). Two fingers pinch it continuously and move it, Ctrl/Cmd+wheel
 * zooms where the pointer is, the presets and +/- step it, and the Move tool
 * (or a middle-button drag) scrolls it with one pointer. A polygon's corner
 * and a magic-fill seed are taken when the finger lifts, so a pinch's first
 * finger leaves nothing behind.
 *
 * Placement (2026-10-26). Inline (the default) everything is one column: the
 * toolbar, the help, the zoom bar, the picture, the colour key. With
 * placement="workbench" the canvas is a part of the analysis workbench
 * (components/workbench/, docs/ANALYSIS_WORKBENCH.md section 12; Field Mode's
 * annotate): the picture fills the stage's fit box and its frame never
 * scrolls (the zoom moves the picture inside it, as before); the tools, the
 * layers, the zoom bar and undo / redo / clear go to the toolbar's slots
 * (tools, target, view, edit); the tool's options and help to a Tool options
 * drawer; the specimen, the underside and object kinds, the colour key, the
 * shortcuts and `intro` (the host's own help) to a Parts and help drawer; the
 * notices over the picture. The behaviour is the same in both.
 */
const MAX_CANVAS = 1600;
const CLOSE_DISTANCE = 0.02;
export const BRUSH_SIZES = [0.008, 0.016, 0.03, 0.06];
const ZOOMS = [1, 1.5, 2, 3];
const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const clampZoom = z => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
const TOOLS = ['brush', 'polygon', 'erase', 'trace', 'wand'];
const TOOL_ICONS = { brush: '🖌️', polygon: '✏️', erase: '🧽', trace: '〰️', wand: '🪄' };
// On the workbench, the tools whose settings float beside them while in hand (ToolOptions).
const FLOATING = new Set(['brush', 'erase', 'wand', 'trace']);
const TOOL_KEYS = { KeyB: 'brush', KeyO: 'polygon', KeyE: 'erase', KeyT: 'trace', KeyF: 'wand' };
const MOVE_KEYS = {
  ArrowUp: [0, -1], KeyW: [0, -1], ArrowDown: [0, 1], KeyS: [0, 1],
  ArrowLeft: [-1, 0], KeyA: [-1, 0], ArrowRight: [1, 0], KeyD: [1, 0],
};
const KEY_FALLBACK = {
  Enter: 'Enter', ' ': 'Space', Spacebar: 'Space', Backspace: 'Backspace', Escape: 'Escape',
  ArrowUp: 'ArrowUp', ArrowDown: 'ArrowDown', ArrowLeft: 'ArrowLeft', ArrowRight: 'ArrowRight',
  1: 'Digit1', 2: 'Digit2', 3: 'Digit3', 4: 'Digit4', '[': 'BracketLeft', ']': 'BracketRight',
};
export const TOUCH_OFFSET_PX = 56;
const STEER_SMOOTHING = 0.35;
const STEER_MAX_STEP = 24;
const JOYSTICK_SPEED = 6;
const AUTO_ANCHOR = 30;
const STREAK_WINDOW_MS = 30000;
const CELEBRATION_MS = 700;

const nextFrame = cb => (window.requestAnimationFrame ? window.requestAnimationFrame(cb) : window.setTimeout(() => cb(Date.now()), 16));
const cancelFrame = id => (window.cancelAnimationFrame ? window.cancelAnimationFrame(id) : window.clearTimeout(id));
const now = () => (window.performance && window.performance.now ? window.performance.now() : Date.now());
const reducedMotion = () => {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  } catch {
    return false;
  }
};
const clampTo = (v, max) => Math.min(max - 0.5, Math.max(0.5, v));

// The artifact layer: gray with dark diagonal stripes, so it is told apart by
// pattern, not by colour alone.
function hatchStyle(ctx) {
  const fallback = LAYER_COLOURS[ARTIFACT_LAYER];
  const tile = document.createElement('canvas');
  tile.width = 12;
  tile.height = 12;
  const tctx = tile.getContext ? tile.getContext('2d') : null;
  if (!tctx || !ctx.createPattern) return fallback;
  tctx.fillStyle = fallback;
  tctx.fillRect(0, 0, 12, 12);
  tctx.strokeStyle = ARTIFACT_STRIPE;
  tctx.lineWidth = 3;
  tctx.beginPath();
  [-12, 0, 12].forEach(offset => { tctx.moveTo(offset, 12); tctx.lineTo(offset + 12, 0); });
  tctx.stroke();
  return ctx.createPattern(tile, 'repeat') || fallback;
}
const layerStyle = (ctx, layer) => (layer === ARTIFACT_LAYER ? hatchStyle(ctx) : LAYER_COLOURS[layer]);
const swatchOf = name => (name === ARTIFACT_LAYER ? ARTIFACT_HATCH_CSS : LAYER_COLOURS[name]);

function paintShapes(ctx, shapes, width, height, colour) {
  ctx.fillStyle = colour;
  ctx.strokeStyle = colour;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  shapes.forEach(shape => {
    ctx.globalCompositeOperation = shape.erase ? 'destination-out' : 'source-over';
    const pts = shape.points;
    if (shape.kind === 'polygon') {
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i ? ctx.lineTo(x * width, y * height) : ctx.moveTo(x * width, y * height)));
      ctx.closePath();
      ctx.fill();
    } else if (pts.length === 1) {
      ctx.beginPath();
      ctx.arc(pts[0][0] * width, pts[0][1] * height, (shape.width * width) / 2, 0, Math.PI * 2);
      ctx.fill();
    } else {
      ctx.lineWidth = shape.width * width;
      ctx.beginPath();
      pts.forEach(([x, y], i) => (i ? ctx.lineTo(x * width, y * height) : ctx.moveTo(x * width, y * height)));
      ctx.stroke();
    }
  });
  ctx.globalCompositeOperation = 'source-over';
}

export default function AnnotationCanvas({
  src, value = [], onChange, others = [], showOthers = false, readOnly = false, onImageSize,
  placement = 'inline', intro = null,
}) {
  const { t } = useTranslation(['annotate', 'common']);
  const canvasRef = useRef(null);
  const frameRef = useRef(null);
  const imgRef = useRef(null);
  const [size, setSize] = useState({ width: 1000, height: 750 });
  const [grid, setGrid] = useState({ w: 1000, h: 750 });
  const [tool, setTool] = useState('brush');
  const [layer, setLayer] = useState('cap');
  const [fertileType, setFertileType] = useState('gills');
  const [artifactLabel, setArtifactLabel] = useState('hand');
  const [specimen, setSpecimen] = useState(1);
  const [brush, setBrush] = useState(BRUSH_SIZES[1]);
  const [zoom, setZoomState] = useState(1);
  const zoomRef = useRef(1);
  // Where the frame should scroll once a new zoom is laid out: {left, top}.
  const scrollTo = useRef(null);
  // Two fingers on the picture: {startZoom, distance, focal: {fx, fy}}.
  const pinch = useRef(null);
  const touches = useRef(new Map());
  // A one-pointer scroll (the Move tool, a middle-button drag): {x, y}.
  const dragScroll = useRef(null);
  const [moving, setMoving] = useState(false);
  // A polygon corner or a magic-fill seed waiting for its finger to lift.
  const pendingTap = useRef(null);
  const [draft, setDraft] = useState([]);
  const [liveStroke, setLiveStroke] = useState(null);
  const [magnet, setMagnet] = useState(true);
  const [xonix, setXonix] = useState(true);
  const [joystick, setJoystick] = useState(false);
  const [tolerance, setTolerance] = useState(12);
  const [pixels, setPixels] = useState('loading');
  const [trace, setTraceState] = useState(emptyTrace);
  const [preview, setPreview] = useState(null);
  const [loupe, setLoupe] = useState(null);
  const [badge, setBadge] = useState(null);
  const [notice, setNotice] = useState('');
  const [, setAnimTick] = useState(0);
  const [, setVersion] = useState(0);
  const stroke = useRef(null);
  const history = useRef({ past: [], future: [] });
  const traceRef = useRef(emptyTrace());
  const steering = useRef(null);
  const joy = useRef({ active: false, vec: [0, 0] });
  const keySegment = useRef(false);
  const keyRepeat = useRef(0);
  const snapOffset = useRef(null);
  const analysisRef = useRef(null);
  const costRef = useRef(null);
  const maskCache = useRef(null);
  const lastWand = useRef(null);
  const celebration = useRef(null);
  const streak = useRef({ count: 0, last: 0 });
  const loopId = useRef(null);
  const pointers = useRef(new Set());
  const live = useRef({});
  live.current = { value, onChange, specimen, layer, fertileType, artifactLabel, magnet, xonix, grid, tolerance, tool };
  // Foreign objects belong to the photograph, not to the specimen being drawn.
  const slotSpecimen = layer === ARTIFACT_LAYER ? ARTIFACT_SPECIMEN : specimen;

  const setTrace = (next) => {
    traceRef.current = next;
    setTraceState(next);
  };

  // ── history ────────────────────────────────────────────────────────────
  const pushHistory = (next) => {
    const { value: current, onChange: change } = live.current;
    if (next === current) return false;
    history.current.past.push(current);
    history.current.future = [];
    lastWand.current = null;
    setVersion(v => v + 1);
    change(next);
    return true;
  };
  const undo = () => {
    const { past, future } = history.current;
    if (!past.length) return;
    future.push(value);
    lastWand.current = null;
    setVersion(v => v + 1);
    onChange(past.pop());
  };
  const redo = () => {
    const { past, future } = history.current;
    if (!future.length) return;
    past.push(value);
    lastWand.current = null;
    setVersion(v => v + 1);
    onChange(future.pop());
  };

  const slotOf = () => ({
    specimen: live.current.layer === ARTIFACT_LAYER ? ARTIFACT_SPECIMEN : live.current.specimen,
    layer: live.current.layer,
    fertileType: live.current.fertileType,
  });
  // A shape painted on the artifact layer says what it is; erasing cuts through every kind.
  const labelled = shape => (live.current.layer === ARTIFACT_LAYER && !shape.erase
    ? { ...shape, label: live.current.artifactLabel } : shape);
  const add = shape => pushHistory(addShape(live.current.value, slotOf(), labelled(shape)));

  // ── feedback ───────────────────────────────────────────────────────────
  const celebrate = (shapes, area) => {
    const g = live.current.grid;
    const at = Date.now();
    const count = at - streak.current.last <= STREAK_WINDOW_MS ? streak.current.count + 1 : 1;
    streak.current = { count, last: at };
    setBadge({ id: at, pct: Math.round((area / (g.w * g.h)) * 1000) / 10, layer: live.current.layer, count });
    if (!reducedMotion()) {
      celebration.current = { shapes, layer: live.current.layer, start: now() };
      startLoop();
    }
  };

  useEffect(() => {
    if (!badge) return undefined;
    const id = window.setTimeout(() => setBadge(null), 2600);
    return () => window.clearTimeout(id);
  }, [badge]);

  useEffect(() => {
    if (!notice) return undefined;
    const id = window.setTimeout(() => setNotice(''), 4500);
    return () => window.clearTimeout(id);
  }, [notice]);

  // ── the photograph's pixels ───────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    analysisRef.current = null;
    costRef.current = null;
    setPixels('loading');
    if (typeof window.Image === 'undefined' || !src) return undefined;
    // The pixels are read from a CORS-loaded copy of the photograph, never
    // from the <img> on screen: a canvas that has drawn a cross-origin image
    // without CORS is TAINTED and getImageData throws a SecurityError, which
    // is what used to disable the magic wand and the magnetic lasso. When the
    // CORS load fails -- the origin is not in the bucket's CORS rule, or the
    // browser's cache hands back a copy fetched without CORS headers -- the
    // bytes are fetched afresh (utils/annotation/raster.js fetchPixelImage) and
    // decoded from a blob, which is same-origin and cannot taint anything.
    // Only when that fails too are the pixel tools unavailable.
    const analyse = (source, width0, height0) => {
      const { width, height } = analysisSize(width0, height0);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext ? canvas.getContext('2d') : null;
      if (!ctx) throw new Error('no 2d context');
      ctx.drawImage(source, 0, 0, width, height);
      const data = ctx.getImageData(0, 0, width, height).data;
      analysisRef.current = analyseImage(data, width, height);
      costRef.current = costFromGradient(analysisRef.current.edge);
      setGrid(g => (g.w === width && g.h === height ? g : { w: width, h: height }));
      setPixels('ready');
    };
    const fallback = () => fetchPixelImage(src)
      .then((source) => {
        if (!alive) return;
        analyse(source, source.naturalWidth || source.width, source.naturalHeight || source.height);
        if (typeof source.close === 'function') source.close();
      })
      .catch(() => { if (alive) setPixels('unavailable'); });
    const img = new window.Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => window.setTimeout(() => {
      if (!alive) return;
      try {
        analyse(img, img.naturalWidth, img.naturalHeight);
      } catch {
        fallback();
      }
    }, 0);
    img.onerror = () => { if (alive) fallback(); };
    img.src = src;
    return () => { alive = false; };
  }, [src]);

  // ── the animation / steering loop ─────────────────────────────────────────
  const startLoop = () => {
    if (loopId.current !== null) return;
    const frame = () => {
      loopId.current = null;
      if (live.current.tick()) loopId.current = nextFrame(frame);
    };
    loopId.current = nextFrame(frame);
  };
  useEffect(() => () => { if (loopId.current !== null) cancelFrame(loopId.current); }, []);

  const resetTrace = () => {
    steering.current = null;
    keySegment.current = false;
    snapOffset.current = null;
    setPreview(null);
    setTrace(emptyTrace());
  };

  useEffect(() => { resetTrace(); }, [grid.w, grid.h]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── tracing ───────────────────────────────────────────────────────────
  const slotMask = () => {
    const { value: v, layer: ly, grid: g } = live.current;
    const sp = slotOf().specimen;
    const entry = v.find(e => e.specimen_index === sp && e.layer === ly);
    if (!entry) return null;
    const cached = maskCache.current;
    if (cached && cached.entry === entry && cached.w === g.w && cached.h === g.h) return cached.mask;
    const mask = rasterizeShapes(entry.geometry?.shapes || [], g.w, g.h);
    maskCache.current = { entry, w: g.w, h: g.h, mask };
    return mask;
  };
  const inMask = (mask, [x, y]) => {
    const g = live.current.grid;
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    return ix >= 0 && iy >= 0 && ix < g.w && iy < g.h && mask[iy * g.w + ix] === 1;
  };
  const nearMask = (mask, [x, y], r) => {
    for (let dy = -r; dy <= r; dy += 1) for (let dx = -r; dx <= r; dx += 1) if (inMask(mask, [x + dx, y + dy])) return true;
    return false;
  };

  const capture = (points, close, explicit) => {
    const { value: v, layer: ly, grid: g, xonix: claim } = live.current;
    const sp = slotOf().specimen;
    if (points.length < (close ? 3 : 2)) {
      if (explicit) setNotice('traceTooShort');
      return false;
    }
    const entry = v.find(e => e.specimen_index === sp && e.layer === ly);
    const result = captureLoop({ points, w: g.w, h: g.h, existing: claim && entry ? entry.geometry.shapes : [], xonix: claim, close });
    const shapes = result ? maskToShapes(result.mask, g.w, g.h) : [];
    if (!shapes.length) {
      if (explicit) setNotice('nothingEnclosed');
      return false;
    }
    let next = v;
    shapes.forEach(shape => { next = addShape(next, slotOf(), labelled(shape)); });
    pushHistory(next);
    // The trail is spent, but the cursor stays where it is: carry on from here.
    const at = traceRef.current.cursor;
    resetTrace();
    setTrace(placeCursor(emptyTrace(), at));
    celebrate(shapes, result.area);
    return true;
  };

  const afterMove = (tr) => {
    const points = tracePoints(tr);
    const g = live.current.grid;
    const closure = findClosure(points, { closeRadius: 8, minArea: 0.0005 * g.w * g.h });
    if (closure) {
      capture(closure.loop, true, false);
      return;
    }
    if (live.current.xonix && points.length >= 4) {
      const mask = slotMask();
      if (mask) {
        const head = points[points.length - 1];
        const prev = points[points.length - 2];
        if (inMask(mask, head) && !inMask(mask, prev) && nearMask(mask, points[0], 3) && points.some(p => !inMask(mask, p))) {
          capture(points, false, false);
        }
      }
    }
  };

  const snapped = (point, event) => {
    const a = analysisRef.current;
    if (!live.current.magnet || !a || (event && event.altKey)) return point;
    const result = snapPoint(a, point, { previousOffset: snapOffset.current });
    snapOffset.current = result.offset;
    return result.point;
  };

  const moveByKey = (direction, event) => {
    const g = live.current.grid;
    let tr = traceRef.current;
    if (!tr.cursor) tr = placeCursor(tr, [g.w / 2, g.h / 2]);
    if (!keySegment.current) {
      tr = beginSegment(tr);
      keySegment.current = true;
      keyRepeat.current = 0;
    }
    const base = event.shiftKey ? 1 : (event.ctrlKey || event.metaKey) ? 16 : 4;
    const accel = event.repeat ? Math.min(3, 1 + (keyRepeat.current++) / 8) : 1;
    const p = snapped([clampTo(tr.cursor[0] + direction[0] * base * accel, g.w),
                       clampTo(tr.cursor[1] + direction[1] * base * accel, g.h)], event);
    tr = extendTrace(tr, [p]);
    setTrace(tr);
    afterMove(tr);
  };

  const stepSteering = (s) => {
    const g = live.current.grid;
    let tr = traceRef.current;
    if (!tr.cursor) return;
    if (live.current.magnet && !s.altKey && analysisRef.current && costRef.current) {
      if (distance(s.anchor, s.target) >= AUTO_ANCHOR) {
        const path = liveWire(costRef.current, g.w, g.h, s.anchor, s.target);
        tr = extendTrace(tr, path.slice(1));
        s.anchor = tr.cursor;
        s.preview = null;
        setPreview(null);
        setTrace(tr);
        afterMove(tr);
      } else if (!s.preview || distance(s.previewTarget, s.target) > 2) {
        s.preview = liveWire(costRef.current, g.w, g.h, s.anchor, s.target);
        s.previewTarget = s.target;
        setPreview(s.preview);
      }
      return;
    }
    const c = tr.cursor;
    const dx = s.target[0] - c[0];
    const dy = s.target[1] - c[1];
    const d = Math.hypot(dx, dy);
    if (d <= 0.5) return;
    const step = Math.min(d, STEER_MAX_STEP, d * STEER_SMOOTHING + 0.5);
    tr = extendTrace(tr, [[clampTo(c[0] + (dx / d) * step, g.w), clampTo(c[1] + (dy / d) * step, g.h)]]);
    s.anchor = tr.cursor;
    setTrace(tr);
    afterMove(tr);
  };

  const stepJoystick = () => {
    const [vx, vy] = joy.current.vec;
    if (!vx && !vy) return;
    const g = live.current.grid;
    let tr = traceRef.current;
    if (!tr.cursor) return;
    const p = snapped([clampTo(tr.cursor[0] + vx * JOYSTICK_SPEED, g.w), clampTo(tr.cursor[1] + vy * JOYSTICK_SPEED, g.h)]);
    tr = extendTrace(tr, [p]);
    setTrace(tr);
    afterMove(tr);
  };

  live.current.tick = () => {
    let busy = false;
    if (steering.current) { busy = true; stepSteering(steering.current); }
    if (joy.current.active) { busy = true; stepJoystick(); }
    if (celebration.current) {
      if (now() - celebration.current.start < CELEBRATION_MS) busy = true;
      else celebration.current = null;
      setAnimTick(x => x + 1);
    }
    return busy;
  };

  const onJoystick = (vec, released) => {
    if (readOnly || live.current.tool !== 'trace') return;
    if (released) {
      joy.current = { active: false, vec: [0, 0] };
      setTrace(endSegment(traceRef.current));
      return;
    }
    if (!joy.current.active) {
      const g = live.current.grid;
      let tr = traceRef.current;
      if (!tr.cursor) tr = placeCursor(tr, [g.w / 2, g.h / 2]);
      setTrace(beginSegment(tr));
      joy.current = { active: true, vec };
      startLoop();
    } else {
      joy.current.vec = vec;
    }
  };

  // ── magic fill ─────────────────────────────────────────────────────────
  const applyWand = ({ seed, mode, base, replace, tol }) => {
    const a = analysisRef.current;
    if (!a) return;
    const mask = cleanMask(magicWand(a, seed[0], seed[1], { tolerance: tol }), a.width, a.height);
    const area = countMask(mask);
    const shapes = area >= 4 ? maskToShapes(mask, a.width, a.height, { erase: mode === 'subtract' }) : [];
    if (!shapes.length) { setNotice('wandNothing'); return; }
    let next = base;
    shapes.forEach(shape => { next = addShape(next, slotOf(), labelled(shape)); });
    if (next === base) { setNotice('nothingToErase'); return; }
    if (replace) live.current.onChange(next);
    else pushHistory(next);
    lastWand.current = { seed, mode, base };
    if (mode === 'add' && !replace) celebrate(shapes, area);
  };

  const changeTolerance = (next) => {
    const tol = Math.max(2, Math.min(60, next));
    setTolerance(tol);
    if (lastWand.current && analysisRef.current) applyWand({ ...lastWand.current, replace: true, tol });
  };

  // ── magnifying ──────────────────────────────────────────────────────────
  /* Zoom to `next`, keeping the picture's point under the client point (cx,
   * cy) -- or the frame's middle -- where it is, then moving it by (dx, dy). */
  const setZoom = useCallback((next, cx, cy, dx = 0, dy = 0) => {
    const frame = frameRef.current;
    const z = clampZoom(next);
    if (frame) {
      const rect = frame.getBoundingClientRect();
      const px = (cx ?? rect.left + rect.width / 2) - rect.left;
      const py = (cy ?? rect.top + rect.height / 2) - rect.top;
      const ratio = z / zoomRef.current;
      scrollTo.current = {
        left: (frame.scrollLeft + px) * ratio - px - dx,
        top: (frame.scrollTop + py) * ratio - py - dy,
      };
    }
    zoomRef.current = z;
    setZoomState(z);
  }, []);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const target = scrollTo.current;
    scrollTo.current = null;
    if (!frame || !target) return;
    frame.scrollLeft = Math.max(0, target.left);
    frame.scrollTop = Math.max(0, target.top);
  }, [zoom]);

  // Ctrl/Cmd+wheel (and a trackpad's pinch, which arrives as one) zooms where
  // the pointer is; a plain wheel scrolls, as it always did. In the workbench the
  // frame never scrolls by itself (overflow hidden), so there the wheel moves the
  // zoomed picture (Shift: sideways).
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) return undefined;
    const onWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        setZoom(zoomRef.current * wheelFactor(e), e.clientX, e.clientY);
        return;
      }
      if (placement !== 'workbench' || zoomRef.current <= 1) return;
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? frame.clientHeight : 1;
      const sideways = e.shiftKey && !e.deltaX;
      frame.scrollLeft += (sideways ? e.deltaY : e.deltaX) * unit;
      frame.scrollTop += (sideways ? 0 : e.deltaY) * unit;
    };
    frame.addEventListener('wheel', onWheel, { passive: false });
    return () => frame.removeEventListener('wheel', onWheel);
  }, [setZoom, placement]);

  const midpoint = () => {
    const [a, b] = [...touches.current.values()];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2, d: Math.hypot(a.x - b.x, a.y - b.y) || 1 };
  };

  /* Two fingers: a pinch, in the capture phase so the canvas never sees the
   * second finger (nor the first one's moves once the pinch has begun). */
  const onFramePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button === 1) {
      event.preventDefault();
      event.stopPropagation();
      dragScroll.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
      return;
    }
    touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (touches.current.size === 2) {
      // What the first finger began is dropped: a pinch draws nothing.
      cancelGesture();
      pendingTap.current = null;
      pointers.current.clear();
      const m = midpoint();
      pinch.current = { startZoom: zoomRef.current, distance: m.d, last: m };
      event.stopPropagation();
      return;
    }
    if (touches.current.size > 2) { event.stopPropagation(); return; }
    if (moving && !readOnly) {
      event.stopPropagation();
      dragScroll.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
    }
  };

  const onFramePointerMove = (event) => {
    if (touches.current.has(event.pointerId)) {
      touches.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }
    if (pinch.current && touches.current.size >= 2) {
      const m = midpoint();
      const g = pinch.current;
      setZoom(g.startZoom * (m.d / g.distance), m.x, m.y, m.x - g.last.x, m.y - g.last.y);
      g.last = m;
      event.stopPropagation();
      event.preventDefault();
      return;
    }
    if (pinch.current) { event.stopPropagation(); return; }
    const drag = dragScroll.current;
    if (drag && drag.id === event.pointerId && frameRef.current) {
      frameRef.current.scrollLeft -= event.clientX - drag.x;
      frameRef.current.scrollTop -= event.clientY - drag.y;
      drag.x = event.clientX;
      drag.y = event.clientY;
      event.stopPropagation();
    }
  };

  const onFramePointerUp = (event) => {
    touches.current.delete(event.pointerId);
    if (pinch.current) {
      event.stopPropagation();
      if (touches.current.size === 0) pinch.current = null;
      return;
    }
    if (dragScroll.current && dragScroll.current.id === event.pointerId) {
      dragScroll.current = null;
      event.stopPropagation();
    }
  };

  // ── pointer input ───────────────────────────────────────────────────────
  const pointOf = (event) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    return [clamp01((event.clientX - rect.left) / rect.width), clamp01((event.clientY - rect.top) / rect.height)];
  };
  const pixelOf = (event, offset = 0) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const g = live.current.grid;
    return [clampTo(((event.clientX - rect.left) / rect.width) * g.w, g.w),
            clampTo(((event.clientY - offset - rect.top) / rect.height) * g.h, g.h)];
  };
  const showLoupe = (event, offset) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    setLoupe({ left: Math.max(0, x - 60), top: Math.max(0, y - offset - 150),
               point: [clamp01(x / rect.width), clamp01((y - offset) / rect.height)] });
  };

  const closeOutline = () => {
    const points = dedupe(draft, 0.003);
    if (points.length >= 3) add({ kind: 'polygon', points: simplifyPath(points, 0) });
    setDraft([]);
  };

  const cancelGesture = () => {
    if (steering.current) {
      steering.current = null;
      setPreview(null);
      setTrace(undoSegment(traceRef.current));
    }
    stroke.current = null;
    setLiveStroke(null);
    setLoupe(null);
  };

  const onPointerDown = (event) => {
    if (readOnly) return;
    pointers.current.add(event.pointerId);
    if (pointers.current.size > 1) { cancelGesture(); return; }
    frameRef.current?.focus?.({ preventScroll: true });
    const touch = event.pointerType === 'touch';
    const offset = touch && (tool === 'trace' || tool === 'wand') ? TOUCH_OFFSET_PX : 0;
    if (touch) showLoupe(event, offset);
    if (event.currentTarget.setPointerCapture && event.pointerId !== undefined) {
      try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* not capturable */ }
    }
    if (tool === 'trace') {
      const p = pixelOf(event, offset);
      if (!p) return;
      let tr = traceRef.current;
      if (!tr.cursor || !tr.segments.length) tr = placeCursor(tr, p);
      tr = beginSegment(tr);
      setTrace(tr);
      steering.current = { target: p, anchor: tr.cursor, offset, altKey: event.altKey, preview: null };
      startLoop();
      return;
    }
    if (tool === 'wand') {
      if (!analysisRef.current) {
        setNotice(pixels === 'unavailable' ? 'pixelsUnavailable' : 'pixelsLoading');
        return;
      }
      const p = pixelOf(event, offset);
      // Filled when the finger lifts: a pinch's first finger fills nothing.
      if (p) pendingTap.current = { id: event.pointerId, kind: 'wand', p, mode: event.altKey ? 'subtract' : 'add' };
      return;
    }
    const p = pointOf(event);
    if (!p) return;
    if (tool === 'polygon') {
      pendingTap.current = { id: event.pointerId, kind: 'polygon', p };
      return;
    }
    stroke.current = { kind: 'stroke', width: brush, ...(tool === 'erase' ? { erase: true } : {}), points: [p] };
    setLiveStroke(stroke.current);
  };

  const onPointerMove = (event) => {
    if (steering.current) {
      const p = pixelOf(event, steering.current.offset);
      if (p) steering.current.target = p;
      steering.current.altKey = event.altKey;
      if (event.pointerType === 'touch') showLoupe(event, steering.current.offset);
      return;
    }
    if (event.pointerType === 'touch' && loupe) showLoupe(event, 0);
    if (!stroke.current) return;
    const p = pointOf(event);
    if (!p) return;
    const points = stroke.current.points;
    if (distance(points[points.length - 1], p) < 0.002) return;
    stroke.current = { ...stroke.current, points: [...points, p] };
    setLiveStroke(stroke.current);
  };

  const onPointerUp = (event) => {
    pointers.current.delete(event.pointerId);
    setLoupe(null);
    const tap = pendingTap.current;
    pendingTap.current = null;
    if (tap && event.type === 'pointerup' && (tap.id === undefined || tap.id === event.pointerId)) {
      if (tap.kind === 'wand') {
        applyWand({ seed: tap.p, mode: tap.mode, base: live.current.value, replace: false, tol: tolerance });
      } else if (draft.length >= 3 && distance(tap.p, draft[0]) < CLOSE_DISTANCE) closeOutline();
      else setDraft(d => [...d, tap.p]);
      return;
    }
    const s = steering.current;
    if (s) {
      steering.current = null;
      setPreview(null);
      const g = live.current.grid;
      let tr = traceRef.current;
      if (tr.segments.length && live.current.magnet && !s.altKey && costRef.current && distance(s.anchor, s.target) > 1) {
        tr = extendTrace(tr, liveWire(costRef.current, g.w, g.h, s.anchor, s.target).slice(1));
        setTrace(tr);
        afterMove(tr);
        tr = traceRef.current;
      }
      setTrace(endSegment(tr));
      return;
    }
    if (!stroke.current) return;
    const finished = stroke.current;
    stroke.current = null;
    setLiveStroke(null);
    add({ ...finished, points: simplifyPath(finished.points, 0.0015) });
  };

  // ── keyboard ────────────────────────────────────────────────────────────
  const chooseTool = (name) => {
    setTool(name);
    setDraft([]);
    setNotice('');
    lastWand.current = null;
  };
  const chooseLayer = (name) => {
    setLayer(name);
    setDraft([]);
    resetTrace();
  };
  const newSpecimen = () => {
    const specimens = specimenIndexes(value);
    setSpecimen(Math.min((specimens.length ? Math.max(...specimens) : 0) + 1, 50));
    setDraft([]);
    resetTrace();
  };

  const onKeyDown = (event) => {
    if (readOnly) return;
    const code = event.code || KEY_FALLBACK[event.key] || '';
    const mod = event.ctrlKey || event.metaKey;
    if (mod && code === 'KeyZ') {
      event.preventDefault();
      if (event.shiftKey) redo(); else undo();
      return;
    }
    if (mod && code === 'KeyY') {
      event.preventDefault();
      redo();
      return;
    }
    if (tool === 'trace' && MOVE_KEYS[code]) {
      event.preventDefault();
      moveByKey(MOVE_KEYS[code], event);
      return;
    }
    if (mod) return;
    if (code === 'Digit1' || code === 'Numpad1') { chooseLayer('cap'); return; }
    if (code === 'Digit2' || code === 'Numpad2') { chooseLayer('stipe'); return; }
    if (code === 'Digit3' || code === 'Numpad3') {
      if (event.shiftKey) setFertileType(ft => FERTILE_TYPES[(FERTILE_TYPES.indexOf(ft) + 1) % FERTILE_TYPES.length]);
      if (layer !== 'fertile_layer') chooseLayer('fertile_layer');
      return;
    }
    if (code === 'Digit4' || code === 'Numpad4') {
      if (event.shiftKey) setArtifactLabel(l => ARTIFACT_LABELS[(ARTIFACT_LABELS.indexOf(l) + 1) % ARTIFACT_LABELS.length]);
      if (layer !== ARTIFACT_LAYER) chooseLayer(ARTIFACT_LAYER);
      return;
    }
    if (TOOL_KEYS[code]) { chooseTool(TOOL_KEYS[code]); return; }
    if (code === 'KeyM') { setMagnet(m => !m); return; }
    if (code === 'KeyN') { newSpecimen(); return; }
    if (code === 'BracketLeft' || code === 'BracketRight') {
      const up = code === 'BracketRight';
      if (tool === 'wand') changeTolerance(tolerance + (up ? 2 : -2));
      else setBrush(b => BRUSH_SIZES[Math.max(0, Math.min(BRUSH_SIZES.length - 1, BRUSH_SIZES.indexOf(b) + (up ? 1 : -1)))]);
      return;
    }
    if (code === 'Space' || code === 'Enter') {
      if (tool === 'trace') {
        event.preventDefault();
        capture(tracePoints(traceRef.current), true, true);
      } else if (tool === 'polygon') {
        event.preventDefault();
        closeOutline();
      }
      return;
    }
    if (code === 'Backspace') {
      if (tool === 'trace') {
        event.preventDefault();
        keySegment.current = false;
        setTrace(undoSegment(traceRef.current));
      } else if (tool === 'polygon' && draft.length) {
        event.preventDefault();
        setDraft(d => d.slice(0, -1));
      }
      return;
    }
    if (code === 'Escape') {
      if (tool === 'trace') resetTrace();
      else setDraft([]);
    }
  };

  const onKeyUp = (event) => {
    const code = event.code || KEY_FALLBACK[event.key] || '';
    if (tool === 'trace' && MOVE_KEYS[code] && keySegment.current) {
      keySegment.current = false;
      keyRepeat.current = 0;
      setTrace(endSegment(traceRef.current));
    }
  };

  const onImageLoad = (event) => {
    const { naturalWidth: w, naturalHeight: h } = event.currentTarget;
    if (!w || !h) return;
    const scale = Math.min(1, MAX_CANVAS / Math.max(w, h));
    setSize({ width: Math.round(w * scale), height: Math.round(h * scale) });
    const analysis = analysisSize(w, h);
    setGrid(g => (g.w === analysis.width && g.h === analysis.height ? g : { w: analysis.width, h: analysis.height }));
    if (onImageSize) onImageSize({ width: w, height: h });
  };

  // ── drawing ───────────────────────────────────────────────────────────
  const drawPolygonPath = useCallback((ctx, points, sx, sy) => {
    ctx.beginPath();
    points.forEach(([x, y], i) => (i ? ctx.lineTo(x * sx, y * sy) : ctx.moveTo(x * sx, y * sy)));
    ctx.closePath();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas && canvas.getContext ? canvas.getContext('2d') : null;
    if (!ctx) return;
    const { width, height } = size;
    ctx.clearRect(0, 0, width, height);

    const paintEntry = (entry, alpha, extraShape) => {
      const layerCanvas = document.createElement('canvas');
      layerCanvas.width = width;
      layerCanvas.height = height;
      const lctx = layerCanvas.getContext('2d');
      if (!lctx) return;
      const shapes = [...(entry.geometry?.shapes || []), ...(extraShape ? [extraShape] : [])];
      paintShapes(lctx, shapes, width, height, layerStyle(lctx, entry.layer));
      ctx.globalAlpha = alpha;
      ctx.drawImage(layerCanvas, 0, 0);
      ctx.globalAlpha = 1;
    };

    if (showOthers) others.forEach(person => person.entries.forEach(entry => paintEntry(entry, 0.22)));
    let liveDrawn = false;
    // Foreign objects last: where they overlap a mushroom part they are what the
    // camera saw, and what the training export keeps.
    [...value].sort((a, b) => (a.layer === ARTIFACT_LAYER) - (b.layer === ARTIFACT_LAYER)).forEach(entry => {
      const current = entry.specimen_index === slotSpecimen && entry.layer === layer;
      if (current && liveStroke) liveDrawn = true;
      const alpha = entry.layer === ARTIFACT_LAYER ? 0.6 : (entry.specimen_index === specimen ? 0.55 : 0.35);
      paintEntry(entry, alpha, current ? liveStroke : null);
    });
    if (liveStroke && !liveDrawn && !liveStroke.erase) {
      paintEntry({ specimen_index: slotSpecimen, layer, geometry: { shapes: [] } }, 0.55, liveStroke);
    }

    specimenIndexes(value).forEach(index => {
      const box = boundingBox(value.filter(e => e.specimen_index === index).flatMap(e => e.geometry?.shapes || []));
      if (!box) return;
      const x = box.minX * width;
      const y = box.minY * height;
      const r = Math.max(10, width * 0.014);
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = index === specimen ? '#111827' : 'rgba(17,24,39,0.7)';
      ctx.fill();
      ctx.fillStyle = '#ffffff';
      ctx.font = `bold ${Math.round(r * 1.2)}px sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(String(index), x, y);
    });

    const colour = LAYER_COLOURS[layer];
    if (draft.length) {
      ctx.strokeStyle = colour;
      ctx.fillStyle = colour;
      ctx.lineWidth = Math.max(2, width * 0.003);
      ctx.setLineDash?.([8, 6]);
      ctx.beginPath();
      draft.forEach(([x, y], i) => (i ? ctx.lineTo(x * width, y * height) : ctx.moveTo(x * width, y * height)));
      ctx.stroke();
      ctx.setLineDash?.([]);
      draft.forEach(([x, y], i) => {
        ctx.beginPath();
        ctx.arc(x * width, y * height, i === 0 ? width * 0.008 : width * 0.005, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    // The trace: a glowing trail, the magnet's live-wire preview, and the cursor.
    const sx = width / grid.w;
    const sy = height / grid.h;
    const trail = tracePoints(trace);
    if (tool === 'trace' && (trail.length || trace.cursor)) {
      ctx.save();
      ctx.shadowColor = colour;
      ctx.shadowBlur = 10;
      ctx.strokeStyle = colour;
      ctx.lineWidth = Math.max(3, width * 0.004);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      if (trail.length > 1) {
        ctx.beginPath();
        trail.forEach(([x, y], i) => (i ? ctx.lineTo(x * sx, y * sy) : ctx.moveTo(x * sx, y * sy)));
        ctx.stroke();
      }
      if (preview && preview.length > 1) {
        ctx.setLineDash?.([6, 5]);
        ctx.beginPath();
        preview.forEach(([x, y], i) => (i ? ctx.lineTo(x * sx, y * sy) : ctx.moveTo(x * sx, y * sy)));
        ctx.stroke();
        ctx.setLineDash?.([]);
      }
      if (trace.cursor) {
        const r = Math.max(7, width * 0.011);
        ctx.beginPath();
        ctx.arc(trace.cursor[0] * sx, trace.cursor[1] * sy, r, 0, Math.PI * 2);
        ctx.fillStyle = colour;
        ctx.fill();
        ctx.shadowBlur = 0;
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#ffffff';
        ctx.stroke();
      }
      ctx.restore();
    }

    // Completion: the new area fills outward from its centre, its outline glowing and fading.
    const c = celebration.current;
    if (c) {
      const p = Math.min(1, (now() - c.start) / CELEBRATION_MS);
      const box = boundingBox(c.shapes);
      if (box) {
        const cx = ((box.minX + box.maxX) / 2) * width;
        const cy = ((box.minY + box.maxY) / 2) * height;
        const reach = Math.hypot((box.maxX - box.minX) * width, (box.maxY - box.minY) * height) / 2 + 2;
        const tone = LAYER_COLOURS[c.layer];
        ctx.save();
        ctx.beginPath();
        ctx.arc(cx, cy, reach * Math.min(1, p * 1.6), 0, Math.PI * 2);
        ctx.clip();
        ctx.globalAlpha = 0.45 * (1 - p * 0.6);
        ctx.fillStyle = tone;
        c.shapes.forEach(shape => { drawPolygonPath(ctx, shape.points, width, height); ctx.fill(); });
        ctx.restore();
        ctx.save();
        ctx.shadowColor = tone;
        ctx.shadowBlur = 24 * (1 - p);
        ctx.strokeStyle = tone;
        ctx.globalAlpha = 1 - p * 0.7;
        ctx.lineWidth = Math.max(2, width * 0.004);
        c.shapes.forEach(shape => { drawPolygonPath(ctx, shape.points, width, height); ctx.stroke(); });
        ctx.restore();
      }
    }
  });

  const specimens = specimenIndexes(value);
  const nextSpecimen = (specimens.length ? Math.max(...specimens) : 0) + 1;
  const specimenChoices = [...new Set([...specimens, specimen])].sort((a, b) => a - b);
  const cursorAttr = trace.cursor
    ? `${(trace.cursor[0] / grid.w).toFixed(3)},${(trace.cursor[1] / grid.h).toFixed(3)}` : '';
  const needsPixels = tool === 'wand' || (tool === 'trace' && magnet);
  const inBench = placement === 'workbench';
  const layerTips = {
    cap: t('tip.layers.cap'),
    stipe: t('tip.layers.stipe'),
    fertile_layer: t('tip.layers.fertile_layer'),
    artifact: t('tip.layers.artifact'),
  };
  const toolTips = {
    brush: t('tip.tools.brush'),
    polygon: t('tip.tools.polygon'),
    erase: t('tip.tools.erase'),
    trace: t('tip.tools.trace'),
    wand: t('tip.tools.wand'),
  };
  const canClear = value.some(e => e.specimen_index === slotSpecimen && e.layer === layer);
  const clearLayer = () => pushHistory(removeLayer(value, slotSpecimen, layer));

  // ── the parts, placed in a column (inline) or over the workbench (placement="workbench") ──
  const fertileSelect = (
    <select aria-label={t('fertileTypeLabel')} value={fertileType} onChange={e => setFertileType(e.target.value)}
            title={t('tip.fertileType')} style={selectStyle}>
      {FERTILE_TYPES.map(type => <option key={type} value={type}>{t(`fertileType.${type}`)}</option>)}
    </select>
  );
  const artifactSelect = (
    <select aria-label={t('artifactLabelLabel')} value={artifactLabel} onChange={e => setArtifactLabel(e.target.value)}
            title={t('tip.artifactLabel')} style={selectStyle}>
      {ARTIFACT_LABELS.map(kind => <option key={kind} value={kind}>{t(`artifactLabels.${kind}`)}</option>)}
    </select>
  );
  const brushControl = (
    <label style={labelStyle}>
      {t('brushSize')}
      <select value={brush} onChange={e => setBrush(Number(e.target.value))} title={t('tip.brushSize')} style={selectStyle}>
        {BRUSH_SIZES.map((s, i) => <option key={s} value={s}>{i + 1}</option>)}
      </select>
    </label>
  );
  const toleranceControl = (
    <label style={labelStyle}>
      {t('tolerance')}
      <input type="range" min="2" max="60" step="1" value={tolerance} aria-label={t('tolerance')}
             title={t('tip.tolerance')}
             onChange={e => changeTolerance(Number(e.target.value))} />
      <span style={{ minWidth: 22 }}>{tolerance}</span>
    </label>
  );
  const traceOptions = (
    <div role="group" aria-label={t('traceOptions')} style={groupStyle}>
      <button type="button" aria-pressed={magnet} onClick={() => setMagnet(m => !m)} title={t('magnetHelp')} style={chip(magnet)}>
        🧲 {t('magnet')}
      </button>
      <button type="button" aria-pressed={xonix} onClick={() => setXonix(x => !x)} title={t('tip.xonix')} style={chip(xonix)}>
        {t('xonix')}
      </button>
      <button type="button" aria-pressed={joystick} onClick={() => setJoystick(j => !j)} title={t('tip.joystick')} style={chip(joystick)}>
        🕹 {t('joystick')}
      </button>
    </div>
  );
  const specimenGroup = (
    <div role="group" aria-label={t('specimen')} style={groupStyle}>
      <label style={labelStyle}>
        {t('specimen')}
        <select aria-label={t('specimen')} value={specimen} disabled={layer === ARTIFACT_LAYER}
                title={layer === ARTIFACT_LAYER ? t('specimenArtifactNote') : t('tip.specimen')}
                onChange={e => { setSpecimen(Number(e.target.value)); setDraft([]); resetTrace(); }} style={selectStyle}>
          {specimenChoices.map(n => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <button type="button" onClick={newSpecimen} style={chip(false)} disabled={nextSpecimen > 50} title={t('tip.addSpecimen')}>
        {t('addSpecimen')}
      </button>
    </div>
  );
  const artifactHelp = !readOnly && layer === ARTIFACT_LAYER && (
    <p data-testid="annotation-artifact-help" style={helpStyle}>{t('artifactHelp')}</p>
  );
  const pixelsNote = !readOnly && needsPixels && pixels !== 'ready' && (
    <p role="note" data-testid="annotation-pixels" style={noteStyle}>
      {t(pixels === 'unavailable' ? 'notices.pixelsUnavailable' : 'notices.pixelsLoading')}
    </p>
  );
  const noticeLine = notice && <p role="status" data-testid="annotation-notice" style={noteStyle}>{t(`notices.${notice}`)}</p>;
  const legend = (
    <ul aria-label={t('legend')} style={{ display: 'flex', gap: 12, flexWrap: 'wrap', listStyle: 'none', margin: 0, padding: 0, fontSize: 12 }}>
      {LAYERS.map(name => (
        <li key={name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span aria-hidden="true" style={{ ...swatchStyle, background: swatchOf(name) }} />
          {t(`layers.${name}`)}
        </li>
      ))}
    </ul>
  );
  const hotkeys = !readOnly && (
    <details style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>
      <summary style={{ cursor: 'pointer' }}>{t('hotkeys.title')}</summary>
      <ul style={{ margin: '4px 0 0', paddingInlineStart: 18 }}>
        {['layers', 'tools', 'magnet', 'close', 'undo', 'other'].map(key => <li key={key}>{t(`hotkeys.${key}`)}</li>)}
      </ul>
    </details>
  );

  // The photograph and what is drawn on it. Inline, its frame scrolls when
  // zoomed; in the workbench it fills the stage's fit box and never scrolls
  // (overflow hidden): the zoom moves the picture inside it, as before.
  const surface = (
    <div style={inBench ? benchSurfaceStyle : { position: 'relative' }}>
      <div ref={frameRef} tabIndex={0} onKeyDown={onKeyDown} onKeyUp={onKeyUp} aria-label={t('canvasLabel')}
           data-testid="annotation-frame" data-zoom={String(Math.round(zoom * 1000) / 1000)}
           onPointerDownCapture={onFramePointerDown} onPointerMoveCapture={onFramePointerMove}
           onPointerUpCapture={onFramePointerUp} onPointerCancelCapture={onFramePointerUp}
           style={inBench ? benchFrameStyle
             : { overflow: 'auto', maxHeight: '65vh', border: '1px solid var(--c-border)', borderRadius: 8, background: '#111827', outline: 'none' }}>
        <div style={{ position: 'relative', width: `${zoom * 100}%`, lineHeight: 0 }}>
          <img ref={imgRef} src={src} alt="" onLoad={onImageLoad} draggable={false}
               style={{ width: '100%', display: 'block', userSelect: 'none' }} />
          <canvas
            ref={canvasRef}
            data-testid="annotation-surface"
            data-cursor={cursorAttr}
            data-trail-points={tracePoints(trace).length}
            width={size.width}
            height={size.height}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onDoubleClick={() => { if (tool === 'polygon' && !readOnly) closeOutline(); }}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%',
                     cursor: readOnly ? 'default' : (moving ? 'grab' : (tool === 'wand' ? 'copy' : 'crosshair')),
                     touchAction: readOnly ? 'auto' : 'none' }}
          />
          {loupe && <Loupe imageRef={imgRef} overlayRef={canvasRef} point={loupe.point} left={loupe.left} top={loupe.top} />}
        </div>
      </div>
      {!readOnly && tool === 'trace' && joystick && (
        <div style={{ position: 'absolute', left: 12, bottom: 12 }}>
          <VirtualJoystick onChange={onJoystick} label={t('joystickLabel')} />
        </div>
      )}
      {badge && (
        <div key={badge.id} data-testid="annotation-streak" aria-live="polite" style={badgeStyle(LAYER_COLOURS[badge.layer])}>
          {t('streak', { pct: badge.pct, layer: t(`layers.${badge.layer}`) })}
          {badge.count > 1 && <> · {t('streakCount', { count: badge.count })}</>}
        </div>
      )}
    </div>
  );

  if (inBench) {
    // The analysis workbench's places (docs/ANALYSIS_WORKBENCH.md, section 12):
    // the tools, the layers, the zoom and the edits on the toolbar; the tool's
    // own options, and the parts' with the colour key and help, in two drawers;
    // the notices over the picture.
    const toolHelp = {
      brush: t('tip.tools.brush'),
      polygon: t('polygonHelp'),
      erase: t('tip.tools.erase'),
      trace: t('traceHelp'),
      wand: t('wandHelp'),
    };
    return (
      <div data-testid="annotation-canvas" data-placement="workbench" style={benchSurfaceStyle}>
        {surface}
        <WorkbenchSlot name="status">
          {pixelsNote}
          {noticeLine}
        </WorkbenchSlot>
        <WorkbenchSlot name="view">
          <div role="group" aria-label={t('zoom')} className="wb-group" data-testid="annotation-zoom-bar">
            {ZOOMS.map(z => (
              <ToolButton key={z} aria-pressed={zoom === z} onClick={() => setZoom(z)} data-testid={`annotation-zoom-${z}`}
                          icon={<bdi dir="ltr">{z}×</bdi>} label={t('tip.zoom', { zoom: z })} />
            ))}
            <span data-testid="annotation-zoom" aria-live="polite" style={{ fontWeight: 600, minWidth: 44, textAlign: 'center' }}>
              <bdi dir="ltr">{Math.round(zoom * 100)}%</bdi>
            </span>
            {!readOnly && (
              <ToolButton aria-pressed={moving} onClick={() => setMoving(m => !m)} data-testid="annotation-move"
                          icon="✋" label={t('common:photoViewer.pan')} title={t('common:tip.pan')} />
            )}
          </div>
        </WorkbenchSlot>
        {!readOnly && (
          <>
            <WorkbenchSlot name="tools">
              <div role="radiogroup" aria-label={t('tool')} className="wb-group" data-testid="annotation-tools">
                {TOOLS.map(name => (
                  <ToolButton key={name} role="radio" aria-checked={tool === name} onClick={() => chooseTool(name)}
                              data-testid={`annotation-tool-${name}`} data-wb-tool={name} icon={TOOL_ICONS[name]}
                              label={t(`tools.${name}`)} title={toolTips[name]} />
                ))}
              </div>
            </WorkbenchSlot>
            {FLOATING.has(tool) && (
              <ToolOptions tool={tool} icon={TOOL_ICONS[tool]} label={t(`tools.${tool}`)} more="options"
                           testId="annotation-float">
                {(tool === 'brush' || tool === 'erase') && (
                  <FloatRange label={t('brushSize')} help={t('tip.brushSize')} min={1} max={BRUSH_SIZES.length}
                              value={Math.max(1, BRUSH_SIZES.indexOf(brush) + 1)} testId="annotation-float-brush"
                              onChange={n => setBrush(BRUSH_SIZES[n - 1])} />
                )}
                {tool === 'wand' && (
                  <ReachSlider label={t('tolerance')} value={tolerance} min={2} max={60} onChange={changeTolerance}
                               testId="annotation-float-reach" />
                )}
                {tool === 'trace' && (
                  <>
                    <FloatCheck label={`🧲 ${t('magnet')}`} help={t('magnetHelp')} checked={magnet} onChange={setMagnet}
                                testId="annotation-float-magnet" />
                    <FloatCheck label={t('xonix')} help={t('tip.xonix')} checked={xonix} onChange={setXonix}
                                testId="annotation-float-xonix" />
                    <FloatCheck label={`🕹 ${t('joystick')}`} help={t('tip.joystick')} checked={joystick}
                                onChange={setJoystick} testId="annotation-float-joystick" />
                  </>
                )}
              </ToolOptions>
            )}
            <WorkbenchSlot name="target">
              <div role="radiogroup" aria-label={t('layer')} className="wb-group" data-testid="annotation-layers">
                {LAYERS.map(name => (
                  <ToolButton key={name} role="radio" aria-checked={layer === name} onClick={() => chooseLayer(name)}
                              data-testid={`annotation-layer-${name}`} label={t(`layers.${name}`)} title={layerTips[name]}
                              icon={<span style={{ ...swatchStyle, background: swatchOf(name) }} />} />
                ))}
              </div>
            </WorkbenchSlot>
            <WorkbenchSlot name="edit">
              <ToolButton onClick={undo} disabled={!history.current.past.length} data-testid="annotation-undo"
                          icon="↶" label={t('undo')} title={t('tip.undo')} />
              <ToolButton onClick={redo} disabled={!history.current.future.length} data-testid="annotation-redo"
                          icon="↷" label={t('redo')} title={t('tip.redo')} />
              <ToolButton onClick={clearLayer} disabled={!canClear} data-testid="annotation-clear"
                          icon="🗑️" label={t('clearLayer')} title={t('tip.clearLayer')} />
            </WorkbenchSlot>
            <WorkbenchDrawer id="options" side="end" order={30} icon="🎛️" title={t('bench.options')} testId="annotation-options">
              <p data-testid="annotation-tool-help" style={helpStyle}>{toolHelp[tool]}</p>
              {(tool === 'brush' || tool === 'erase') && brushControl}
              {tool === 'wand' && toleranceControl}
              {tool === 'trace' && traceOptions}
            </WorkbenchDrawer>
          </>
        )}
        <WorkbenchDrawer id="parts" side="end" order={20} icon="🎨" title={t('bench.parts')} testId="annotation-parts">
          {intro}
          {!readOnly && specimenGroup}
          {!readOnly && layer === 'fertile_layer' && <label style={labelStyle}>{t('fertileTypeLabel')}{fertileSelect}</label>}
          {!readOnly && layer === ARTIFACT_LAYER && <label style={labelStyle}>{t('artifactLabelLabel')}{artifactSelect}</label>}
          {artifactHelp}
          <p style={{ ...helpStyle, fontWeight: 600 }}>{t('legend')}</p>
          {legend}
          {hotkeys}
        </WorkbenchDrawer>
      </div>
    );
  }

  return (
    <div data-testid="annotation-canvas" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {!readOnly && (
        <div role="toolbar" aria-label={t('toolbar')} style={toolbarStyle}>
          <div role="group" aria-label={t('layer')} style={groupStyle}>
            {LAYERS.map(name => (
              <button key={name} type="button" aria-pressed={layer === name} onClick={() => chooseLayer(name)}
                      title={layerTips[name]} style={chip(layer === name, LAYER_COLOURS[name])}>
                <span aria-hidden="true" style={{ ...swatchStyle, background: swatchOf(name) }} />
                {t(`layers.${name}`)}
              </button>
            ))}
            {layer === 'fertile_layer' && fertileSelect}
            {layer === ARTIFACT_LAYER && artifactSelect}
          </div>

          <div role="group" aria-label={t('tool')} style={groupStyle}>
            {TOOLS.map(name => (
              <button key={name} type="button" aria-pressed={tool === name} onClick={() => chooseTool(name)} style={chip(tool === name)}
                      title={toolTips[name]}>
                {t(`tools.${name}`)}
              </button>
            ))}
            {(tool === 'brush' || tool === 'erase') && brushControl}
            {tool === 'wand' && toleranceControl}
          </div>

          {tool === 'trace' && traceOptions}

          {specimenGroup}

          <div role="group" style={groupStyle}>
            <button type="button" onClick={undo} disabled={!history.current.past.length} title={t('tip.undo')} style={chip(false)}>{t('undo')}</button>
            <button type="button" onClick={redo} disabled={!history.current.future.length} title={t('tip.redo')} style={chip(false)}>{t('redo')}</button>
            <button type="button" onClick={clearLayer}
                    disabled={!canClear} title={t('tip.clearLayer')} style={chip(false)}>
              {t('clearLayer')}
            </button>
          </div>
        </div>
      )}

      {!readOnly && tool === 'trace' && <p style={helpStyle}>{t('traceHelp')}</p>}
      {!readOnly && tool === 'wand' && <p style={helpStyle}>{t('wandHelp')}</p>}
      {artifactHelp}
      {pixelsNote}

      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', fontSize: 12 }}>
        <span>{t('zoom')}</span>
        {ZOOMS.map(z => (
          <button key={z} type="button" aria-pressed={zoom === z} onClick={() => setZoom(z)} title={t('tip.zoom', { zoom: z })} style={chip(zoom === z)}>{z}×</button>
        ))}
        <span data-testid="annotation-zoom" aria-live="polite" style={{ fontWeight: 600, minWidth: 44 }}>
          {Math.round(zoom * 100)}%
        </span>
        {!readOnly && (
          <button type="button" aria-pressed={moving} onClick={() => setMoving(m => !m)} data-testid="annotation-move"
            title={t('common:tip.pan')} style={chip(moving)}>✋ {t('common:photoViewer.pan')}</button>
        )}
        {tool === 'polygon' && !readOnly && <span style={{ color: 'var(--c-text-muted-fg)' }}>{t('polygonHelp')}</span>}
      </div>

      {noticeLine}

      {surface}

      {legend}

      {hotkeys}
    </div>
  );
}

const toolbarStyle = { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' };
const groupStyle = { display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center' };
const labelStyle = { display: 'flex', gap: 4, alignItems: 'center', fontSize: 12, color: 'var(--c-text-body-fg)' };
const selectStyle = { fontSize: 13, padding: '3px 6px', borderRadius: 6, border: '1px solid var(--c-border-strong)' };
const swatchStyle = { display: 'inline-block', width: 12, height: 12, borderRadius: 3, border: '1px solid rgba(0,0,0,0.3)' };
const helpStyle = { margin: 0, fontSize: 12, color: 'var(--c-text-muted-fg)' };
// In the workbench: the fit box, filled; a frame that never scrolls (the zoom moves the picture inside it).
const benchSurfaceStyle = { position: 'relative', width: '100%', height: '100%' };
const benchFrameStyle = { overflow: 'hidden', width: '100%', height: '100%', background: '#111827', outline: 'none',
  touchAction: 'none' };
const noteStyle = { margin: 0, fontSize: 12, color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft)', borderRadius: 6, padding: '4px 8px' };
const badgeStyle = colour => ({
  position: 'absolute', top: 10, left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none',
  background: 'rgba(17,24,39,0.85)', color: '#ffffff', border: `2px solid ${colour}`, boxShadow: `0 0 14px ${colour}`,
  borderRadius: 999, padding: '4px 14px', fontSize: 14, fontWeight: 700, whiteSpace: 'nowrap',
});
const chip = (on, colour) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 13, padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
  border: `2px solid ${on ? (colour || 'var(--c-primary)') : 'var(--c-border)'}`,
  background: on ? 'var(--c-primary-soft)' : 'var(--c-surface)', color: 'var(--c-text-fg)', fontWeight: on ? 600 : 400,
});
